import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useI18n } from '@/contexts/I18nContext';
import { RecurrenceConfig, calculateOccurrences, validateRecurrenceConfig } from '@/utils/recurrenceUtils';
import { useToast } from '@/shared/hooks/use-toast';
import { DateInput } from '@/components/ui/DateInput';
import { convertInternalToDisplay, convertDisplayToInternal } from '@/utils/dateTimeInputUtils';

interface RecurrenceSelectorProps {
  value: RecurrenceConfig;
  onChange: (config: RecurrenceConfig) => void;
  maxMonths?: number;
  startDate?: Date; // Initial date to calculate "Weekly on Monday"
}

export const RecurrenceSelector: React.FC<RecurrenceSelectorProps> = ({
  value,
  onChange,
  maxMonths = 3,
  startDate,
}) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomDialog, setShowCustomDialog] = useState(false);

  // Days of week
  const daysOfWeek = [
    { value: 0, label: t('calendar.sunday'), short: 'Sun' },
    { value: 1, label: t('calendar.monday'), short: 'Mon' },
    { value: 2, label: t('calendar.tuesday'), short: 'Tue' },
    { value: 3, label: t('calendar.wednesday'), short: 'Wed' },
    { value: 4, label: t('calendar.thursday'), short: 'Thu' },
    { value: 5, label: t('calendar.friday'), short: 'Fri' },
    { value: 6, label: t('calendar.saturday'), short: 'Sat' },
  ];

  // Get button text based on selection
  const getButtonText = (): string => {
    if (!value.enabled || value.frequency === 'none') {
      return t('calendar.does_not_repeat');
    }

    switch (value.frequency) {
      case 'daily':
        return t('calendar.daily');
      case 'weekly':
        if (value.daysOfWeek?.length === 1 && startDate) {
          const dayIndex = value.daysOfWeek[0];
          const dayName = daysOfWeek[dayIndex]?.label || '';
          return t('calendar.weekly_on', { day: dayName });
        }
        return t('calendar.weekly');
      case 'monthly':
        return t('calendar.monthly');
      case 'annually':
        return t('calendar.annually');
      case 'weekdays':
        return t('calendar.every_weekday');
      case 'custom':
        if (value.daysOfWeek && value.daysOfWeek.length > 0) {
          const selectedDays = value.daysOfWeek
            .map((d) => daysOfWeek[d]?.short || '')
            .filter(Boolean)
            .join(', ');
          return `${t('calendar.weekly')} (${selectedDays})`;
        }
        return t('calendar.custom');
      default:
        return t('calendar.does_not_repeat');
    }
  };

  const handleFrequencySelect = (frequency: RecurrenceConfig['frequency']) => {
    if (frequency === 'custom') {
      setShowCustomDialog(true);
      setIsOpen(false);
      return;
    }

    let daysOfWeek: number[] | undefined;

    // Auto-select day based on initial date
    if (frequency === 'weekly' && startDate) {
      daysOfWeek = [startDate.getDay()];
    } else if (frequency === 'weekdays') {
      daysOfWeek = [1, 2, 3, 4, 5]; // Monday to Friday
    }

    onChange({
      ...value,
      enabled: frequency !== 'none',
      frequency,
      daysOfWeek,
    });

    setIsOpen(false);
  };

  // Calculate max end date
  const getMaxEndDate = (): string => {
    if (!startDate || isNaN(startDate.getTime())) return '';
    const maxDate = new Date(startDate);
    maxDate.setMonth(maxDate.getMonth() + maxMonths);
    // Check if the date is valid before converting to ISO string
    if (isNaN(maxDate.getTime())) return '';
    return maxDate.toISOString().split('T')[0];
  };

  // Calculate occurrences preview
  // Use the same logic as in AddClassModal: when recurrence is enabled, use today's date as base
  // The actual dates will be generated by generateRecurringDates based on the recurrence pattern
  const occurrencesCount = startDate && value.endDate
    ? calculateOccurrences(value, startDate)
    : 0;

  // Validate when endDate changes
  useEffect(() => {
    if (value.enabled && value.endDate && startDate) {
      const validation = validateRecurrenceConfig(value, startDate, maxMonths);
      if (!validation.isValid && validation.error) {
        // Don't show toast on every change, just validate
      }
    }
  }, [value, startDate, maxMonths]);

  return (
    <>
      <div className="space-y-2">
        <Label className="text-black">{t('calendar.recurrence')}</Label>

        {/* Dropdown Button (Google Calendar style) */}
        <div className="w-full border border-gray-300 rounded-md focus-within:border-gray-400 focus-within:ring-1 focus-within:ring-gray-400">
          <Select
            open={isOpen}
            onOpenChange={setIsOpen}
            value={value.enabled && value.frequency !== 'none' ? value.frequency : 'none'}
            onValueChange={(selectedValue) => {
              // Map the string value to the frequency type
              const frequencyMap: Record<string, RecurrenceConfig['frequency']> = {
                'none': 'none',
                'daily': 'daily',
                'weekly': 'weekly',
                'monthly': 'monthly',
                'annually': 'annually',
                'weekdays': 'weekdays',
                'custom': 'custom',
              };
              const frequency = frequencyMap[selectedValue] || 'none';
              handleFrequencySelect(frequency);
            }}
          >
            <SelectTrigger className="w-full text-black !ring-0 !outline-none !shadow-none border-0 focus:!ring-0 focus:!outline-none focus:!shadow-none bg-transparent text-sm [&_svg]:text-gray-500">
              <SelectValue>{getButtonText()}</SelectValue>
            </SelectTrigger>
            <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1 max-h-60">
            <SelectItem
              value="none"
              className="cursor-pointer text-white text-sm"
            >
              {t('calendar.does_not_repeat')}
            </SelectItem>
            <SelectItem
              value="daily"
              className="cursor-pointer text-white text-sm"
            >
              {t('calendar.daily')}
            </SelectItem>
            <SelectItem
              value="weekly"
              className="cursor-pointer text-white text-sm"
            >
              {value.daysOfWeek && value.daysOfWeek.length === 1
                ? t('calendar.weekly_on', {
                    day: daysOfWeek[value.daysOfWeek[0]]?.label || '',
                  })
                : startDate
                ? t('calendar.weekly_on', {
                    day: daysOfWeek[startDate.getDay()]?.label || '',
                  })
                : t('calendar.weekly')}
            </SelectItem>
            <SelectItem
              value="monthly"
              className="cursor-pointer text-white text-sm"
            >
              {t('calendar.monthly')}
            </SelectItem>
            <SelectItem
              value="annually"
              className="cursor-pointer text-white text-sm"
            >
              {t('calendar.annually')}
            </SelectItem>
            <SelectItem
              value="weekdays"
              className="cursor-pointer text-white text-sm"
            >
              {t('calendar.every_weekday')}
            </SelectItem>
            <SelectItem
              value="custom"
              className="cursor-pointer text-white text-sm"
            >
              {t('calendar.custom')}...
            </SelectItem>
          </SelectContent>
          </Select>
        </div>

        {/* Show additional configuration if recurrence is active */}
        {value.enabled && value.frequency !== 'none' && (
          <div className="space-y-2 pt-2">
            {/* End date selector */}
            <div>
              <Label className="text-black text-sm">{t('calendar.repeat_until')}*</Label>
              <DateInput
                id="repeat-until-date"
                value={value.endDate || ''}
                onChange={(internalValue) => {
                  // DateInput provides yyyy-MM-dd format
                  const newConfig = { ...value, endDate: internalValue };
                  onChange(newConfig);
                }}
                onBlur={() => {
                  // Validate only when user finishes editing (on blur)
                  const newEndDate = value.endDate;

                  // Only validate if there's a value and it's a complete date (YYYY-MM-DD format)
                  if (newEndDate && newEndDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    const newConfig = { ...value, endDate: newEndDate };

                    if (startDate) {
                      const validation = validateRecurrenceConfig(
                        newConfig,
                        startDate,
                        maxMonths
                      );
                      if (!validation.isValid && validation.error) {
                        toast({
                          title: 'Validation Error',
                          description: validation.error,
                          variant: 'destructive',
                          duration: 3000,
                        });
                      }
                    }
                  }
                }}
                min={startDate && !isNaN(startDate.getTime()) ? startDate.toISOString().split('T')[0] : undefined}
                max={getMaxEndDate()}
              />
            </div>

            {/* Preview: How many trainings will be created */}
            {value.endDate && occurrencesCount > 0 && (
              <p className="text-xs text-gray-500">
                {t('calendar.will_create_occurrences', { count: occurrencesCount })}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Custom Dialog (for selecting multiple days) */}
      <Dialog open={showCustomDialog} onOpenChange={setShowCustomDialog}>
        <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-black">
              {t('calendar.select_days_of_week')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-2 py-4">
            {daysOfWeek.map((day) => {
              const isChecked = value.daysOfWeek?.includes(day.value) || false;
              return (
                <div key={day.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`day-${day.value}`}
                    checked={isChecked}
                    onCheckedChange={(checked) => {
                      const currentDays = value.daysOfWeek || [];
                      const newDays = checked
                        ? [...currentDays, day.value]
                        : currentDays.filter((d) => d !== day.value);

                      onChange({
                        ...value,
                        enabled: newDays.length > 0,
                        frequency: 'custom',
                        daysOfWeek: newDays.length > 0 ? newDays : undefined,
                      });
                    }}
                    className={`h-4 w-4 rounded border-2 transition-colors ${
                      isChecked
                        ? 'border-sossiety-red bg-white data-[state=checked]:bg-white data-[state=checked]:border-sossiety-red [&>span]:flex [&>span]:items-center [&>span]:justify-center [&>span]:h-full [&>span]:w-full [&>span>svg]:text-gray-500 [&>span>svg]:h-3 [&>span>svg]:w-3 [&>span>svg]:mt-0'
                        : 'border-gray-300 bg-white'
                    }`}
                  />
                  <Label
                    htmlFor={`day-${day.value}`}
                    className="text-sm text-black cursor-pointer"
                  >
                    {day.label}
                  </Label>
                </div>
              );
            })}
          </div>

          <DialogFooter className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowCustomDialog(false)}
              className="flex-1 border-black text-black hover:bg-gray-50 rounded-full"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (value.daysOfWeek && value.daysOfWeek.length > 0) {
                  setShowCustomDialog(false);
                } else {
                  toast({
                    title: 'Error',
                    description: t('calendar.select_at_least_one_day'),
                    variant: 'destructive',
                    duration: 3000,
                  });
                }
              }}
              className="flex-1 bg-sossiety-red hover:bg-sossiety-red-hover text-white rounded-full"
            >
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

